import { useState, useCallback } from 'react';

const STORAGE_KEY = 'snapfy_theme';

/**
 * Theme hook — toggles between dark (default) and light mode.
 * Light mode is applied via the `.light-mode` class on the <html> element and
 * persisted to localStorage. The initial class is set synchronously by a boot
 * script in index.html to avoid a flash of the wrong theme on load.
 */
export const useTheme = () => {
  const [isLightMode, setIsLightMode] = useState<boolean>(
    () =>
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('light-mode'),
  );

  const toggleTheme = useCallback(() => {
    // Derive the next value from the live DOM class (the single source of truth),
    // not from React state, so the toggle stays correct regardless of how many
    // components consume this hook.
    const next = !document.documentElement.classList.contains('light-mode');
    document.documentElement.classList.toggle('light-mode', next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? 'light' : 'dark');
    } catch (e) {
      /* localStorage unavailable */
    }
    setIsLightMode(next);
  }, []);

  return { isLightMode, toggleTheme };
};
